/** @jsxImportSource @emotion/react */
import { durations, easeInOut } from '@atlaskit/motion';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { token } from '@atlaskit/tokens';
import { SerializedStyles, css } from '@emotion/react';
import { Box, Typography, Button } from '@mui/material';
import React, { memo, useContext, useEffect, useRef, useState } from 'react';
import { Camera } from '../types';
import { InstanceIdContext } from './CameraScreen';
import { supabase } from '../utils/supabase';

export interface CameraBoxProps {
    camera: Camera;
    videoRef: (el: HTMLVideoElement | null) => void;
    isValidHttpUrl: (url: string) => boolean;
}

type gridItemStates = 'idle' | 'dragging' | 'over';

const supabaseAPI: string = `${process.env.SUPABASE_URL}/functions/v1`;

async function getUserAccessToken() {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token;
}

const gridItemStyles: SerializedStyles = css({
    objectFit: 'cover',
    width: '100%',
    boxSizing: 'border-box',
    background: token('elevation.surface.raised', '#FFF'),
    padding: token('space.050', '4px'),
    borderRadius: token('border.radius.100', '4px'),
    boxShadow: token('elevation.shadow.raised', 'none'),
    transition: `all ${durations.small}ms ${easeInOut}`,
    '-webkit-touch-callout': 'none', // needed to avoid a "save image" popup on iOS
});


const itemStateStyles: { [Key in gridItemStates]: undefined | SerializedStyles } = {
    idle: css({
        ':hover': {
            background: token('elevation.surface.overlay', '#FFF'),
            boxShadow: token('elevation.shadow.overlay', 'none'),
        },
    }),
    dragging: css({
        filter: 'grayscale(0.8)',
    }),
    over: css({
        transform: 'scale(0.9)',
        filter: 'brightness(1.15)',
        boxShadow: token('elevation.shadow.overlay', 'none'),
    }),
};

const CameraBox: React.FC<CameraBoxProps> = ({
    camera,
    videoRef,
    isValidHttpUrl,
}) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const [state, setState] = useState<gridItemStates>('idle');
    const instanceId = useContext(InstanceIdContext);

    const accessTokenRef = useRef<string | null>(null);

    const extractDeviceSerialFromUrl = (url: string): string | null => {
        const match = url.match(/openlive\/([A-Z0-9]+)_/i);
        return match ? match[1] : null;
    };

    const getValidAccessToken = async (): Promise<string> => {
        const now = Date.now();
        const cachedToken = accessTokenRef.current;

        if (cachedToken) {
            const expiresAt = parseInt(localStorage.getItem('ezviz_token_expire') || '0', 10);
            if (now < expiresAt - 60000) {
                return cachedToken;
            }
        }

        try {
            const response = await fetch(`${supabaseAPI}/fetchEZVIZAccessToken`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${await getUserAccessToken()}`,
                },
            });

            const result = await response.json();
            if (result.error) {
                console.error('Error fetching EZVIZ token:', result.error);
                return Promise.reject(new Error(result.error));
            }

            accessTokenRef.current = result.accessToken;
            const expireInSeconds = Number(result.expireTime);
            if (!isNaN(expireInSeconds)) {
                const expireAt = Date.now() + expireInSeconds * 1000;
                localStorage.setItem('ezviz_token_expire', expireAt.toString());
            } else {
                console.warn('Invalid expire time received:', result.expireTime);
            }

            return result.accessToken;
        } catch (error) {
            console.error('Failed to fetch EZVIZ token:', error);
            throw error;
        }
    };

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        return combine(
            draggable({
                element: el,
                getInitialData: () => ({ type: 'grid-item', id: camera.id, instanceId }),
                onDragStart: () => setState('dragging'),
                onDrop: () => setState('idle'),
            }),
            dropTargetForElements({
                element: el,
                getData: () => ({ id: camera.id }),
                getIsSticky: () => false,
                canDrop: ({ source }) =>
                    source.data.instanceId === instanceId &&
                    source.data.type === 'grid-item' &&
                    source.data.id !== camera.id,
                onDragEnter: () => setState('over'),
                onDragLeave: () => setState('idle'),
                onDrop: () => setState('idle'),
            }),
        );
    }, [camera.id, instanceId]);

    const startPTZControl = async (direction: number, speed: number = 1) => {
        const deviceSerial = extractDeviceSerialFromUrl(camera.stream_url);
        if (!deviceSerial) return console.error('Invalid stream URL');

        try {
            const accessToken = await getValidAccessToken();
            const params = new URLSearchParams({
                accessToken,
                deviceSerial,
                channelNo: '1',
                direction: direction.toString(),
                speed: speed.toString(),
            });

            const response = await fetch(`https://open.ezvizlife.com/api/lapp/device/ptz/start?${params}`, { method: 'POST' });
            const data = await response.json();
            console.log('PTZ Start:', data);
        } catch (error) {
            console.error('PTZ start error:', error);
        }
    };

    const stopPTZControl = async (direction: number) => {
        const deviceSerial = extractDeviceSerialFromUrl(camera.stream_url);
        if (!deviceSerial) return console.error('Invalid stream URL');

        try {
            const accessToken = await getValidAccessToken();
            const params = new URLSearchParams({
                accessToken,
                deviceSerial,
                channelNo: '1',
                direction: direction.toString(),
            });

            const response = await fetch(`https://open.ezvizlife.com/api/lapp/device/ptz/stop?${params}`, { method: 'POST' });
            const data = await response.json();
            console.log('PTZ Stop:', data);
        } catch (error) {
            console.error('PTZ stop error:', error);
        }
    };

    return (
        <div
            // component={"div"}
            ref={ref}
            data-id={camera.id}
            // eslint-disable-next-line react/no-unknown-property
            css={[gridItemStyles, itemStateStyles[state]]}
            style={{
                backgroundColor: 'grey.200',
                border: '1px solid black',
                borderRadius: 1,
                width: '100%',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0px 1px 3px rgba(0,0,0,0.2)',
            }}
        >
            <Typography
                variant="subtitle1"
                sx={{ flex: '0 0 auto', fontWeight: 'bold', p: 1 }}
            >
                {camera.name}
            </Typography>

            <Box sx={{ flex: 1, position: 'relative', justifyContent: 'center', justifyItems: 'center', display: 'flex' }}>
                {isValidHttpUrl(camera.stream_url) ? (
                    <video
                        ref={videoRef}
                        title={camera.name}
                        muted
                        autoPlay
                        playsInline
                        controls
                        style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            border: 'none',
                            borderRadius: 4,
                            objectFit: 'contain',
                        }}
                    />
                ) : (
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'grey.300',
                            borderRadius: 4,
                        }}
                    >
                        <Typography variant="subtitle1" color="text.secondary">
                            No data available
                        </Typography>
                    </Box>
                )}
            </Box>
            {camera.stream_url.includes("ezvizlife.com") && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 1, gap: 1, flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        size="small"
                        onMouseDown={() => startPTZControl(0)}
                        onMouseUp={() => stopPTZControl(0)}
                        onTouchStart={() => startPTZControl(0)}
                        onTouchEnd={() => stopPTZControl(0)}
                        sx={{
                            transition: 'all 0.2s',
                            '&:hover': {
                                backgroundColor: 'primary.main',
                                color: 'white',
                                borderColor: 'primary.main',
                            },
                        }}
                    >
                        Up
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onMouseDown={() => startPTZControl(1)}
                        onMouseUp={() => stopPTZControl(1)}
                        onTouchStart={() => startPTZControl(1)}
                        onTouchEnd={() => stopPTZControl(1)}
                        sx={{
                            transition: 'all 0.2s',
                            '&:hover': {
                                backgroundColor: 'primary.main',
                                color: 'white',
                                borderColor: 'primary.main',
                            },
                        }}
                    >
                        Down
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onMouseDown={() => startPTZControl(2)}
                        onMouseUp={() => stopPTZControl(2)}
                        onTouchStart={() => startPTZControl(2)}
                        onTouchEnd={() => stopPTZControl(2)}
                        sx={{
                            transition: 'all 0.2s',
                            '&:hover': {
                                backgroundColor: 'primary.main',
                                color: 'white',
                                borderColor: 'primary.main',
                            },
                        }}
                    >
                        Left
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onMouseDown={() => startPTZControl(3)}
                        onMouseUp={() => stopPTZControl(3)}
                        onTouchStart={() => startPTZControl(3)}
                        onTouchEnd={() => stopPTZControl(3)}
                        sx={{
                            transition: 'all 0.2s',
                            '&:hover': {
                                backgroundColor: 'primary.main',
                                color: 'white',
                                borderColor: 'primary.main',
                            },
                        }}
                    >
                        Right
                    </Button>
                </Box>
            )}
        </div>
    )
};

export default memo(CameraBox);
